"use client"

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

interface RateLimitContextType {
  isRateLimited: boolean
  setRateLimited: (limited: boolean, retryAfter?: number) => void
  retryAfter: number | null
}

const RateLimitContext = createContext<RateLimitContextType | null>(null)

export const RateLimitProvider = ({ children }: { children: React.ReactNode }) => {
  const [isRateLimited, setIsRateLimited] = useState(false)
  const [retryAfter, setRetryAfter] = useState<number | null>(null)
  const prevIsRateLimitedRef = useRef(false)

  const setRateLimited = useCallback((limited: boolean, retryAfterSeconds?: number) => {
    setIsRateLimited(limited)
    if (limited && retryAfterSeconds) {
      setRetryAfter(retryAfterSeconds)
    } else {
      setRetryAfter(null)
    }
  }, [])

  // Listen to rate limit events from axios/fetch interceptors
  useEffect(() => {
    const handleRateLimit = (event: CustomEvent) => {
      const { isLimited, retryAfter: retryAfterValue } = event.detail
      setRateLimited(isLimited, retryAfterValue)
    }

    window.addEventListener("rateLimit", handleRateLimit as EventListener)

    // Check sessionStorage on mount
    if (typeof window !== "undefined") {
      const rateLimitState = sessionStorage.getItem("rateLimitState")
      if (rateLimitState) {
        try {
          const { isLimited, expiresAt } = JSON.parse(rateLimitState)
          if (isLimited && expiresAt && Date.now() < expiresAt) {
            const remaining = Math.ceil((expiresAt - Date.now()) / 1000)
            setRateLimited(true, remaining)
          }
        } catch {
          // Invalid state, clear it
          sessionStorage.removeItem("rateLimitState")
        }
      }
    }

    return () => {
      window.removeEventListener("rateLimit", handleRateLimit as EventListener)
    }
  }, [setRateLimited])

  // Auto-reset rate limit after retryAfter seconds
  useEffect(() => {
    if (isRateLimited && retryAfter) {
      const timer = setTimeout(() => {
        setIsRateLimited(false)
        setRetryAfter(null)
        if (typeof window !== "undefined") {
          sessionStorage.removeItem("rateLimitState")
        }
      }, retryAfter * 1000)

      return () => clearTimeout(timer)
    }
  }, [isRateLimited, retryAfter])

  // Refresh page when rate limit is cleared (transitions from true to false)
  useEffect(() => {
    // Check if rate limit was just cleared (transitioned from true to false)
    if (prevIsRateLimitedRef.current && !isRateLimited) {
      // Small delay to ensure state is fully cleared before refresh
      const refreshTimer = setTimeout(() => {
        if (typeof window !== "undefined") {
          window.location.reload()
        }
      }, 100)

      return () => clearTimeout(refreshTimer)
    }

    // Update the ref to track current state for next render
    prevIsRateLimitedRef.current = isRateLimited
  }, [isRateLimited])

  return (
    <RateLimitContext.Provider
      value={{
        isRateLimited,
        setRateLimited,
        retryAfter,
      }}
    >
      {children}
    </RateLimitContext.Provider>
  )
}

export const useRateLimit = () => {
  const context = useContext(RateLimitContext)
  if (context === null) {
    throw new Error("useRateLimit must be used within a RateLimitProvider")
  }
  return context
}

